import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useBillingArReport,
    useExportBillingAr,
    type BillingArFilter,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useClaims } from '@/api/claims';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';
import { StatusBadge } from '@/components/ui/Badge';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
const STATUSES = ['Ready', 'Submitted', 'Partial', 'Paid', 'Denied', 'Voided'] as const;

function fmtMoney(n: number): string {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function fmtMoneyCents(n: number): string {
    return n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function fmtPct(n: number): string {
    return `${(n * 100).toFixed(1)}%`;
}

export function BillingArPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [filter, setFilter] = useState<BillingArFilter>({});
    // Local form state — applied to the query only when the user hits Apply.
    // Keeps typing in the date inputs from triggering a fetch per keystroke.
    const [draft, setDraft] = useState<BillingArFilter>({});

    const { data, isLoading, isError, error, isFetching } = useBillingArReport(filter);
    const exportM = useExportBillingAr();
    const clientsQuery = useClientNames();
    // Pull claims just for the payer dropdown. The report server endpoint
    // doesn't need to know payer catalogue separately — we can derive from
    // the claims list hook that's already cached app-wide.
    const claimsQuery = useClaims();

    const payerOptions = useMemo(() => {
        const set = new Set<string>();
        if (claimsQuery.data) for (const c of claimsQuery.data) if (c.payer) set.add(c.payer);
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [claimsQuery.data]);

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [] as { id: string; label: string }[];
        return clientsQuery.data
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    function apply() {
        setFilter({ ...draft });
    }

    function clear() {
        setDraft({});
        setFilter({});
    }

    const hasActiveFilter =
        !!(filter.clientId || filter.payerName || filter.status || filter.dateFrom || filter.dateTo);

    async function runExport() {
        try {
            await exportM.mutateAsync(filter);
        } catch {
            // Toast surface — isError is rendered below.
        }
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Billing & AR</h1>
                    <p className="text-xs text-text-subtle">
                        Collections, aging, payer rollup, and patient-responsibility outstanding.
                    </p>
                </div>
                {canExport && (
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={runExport}
                        disabled={exportM.isPending || isLoading}
                    >
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">Client</span>
                            <select
                                value={draft.clientId ?? ''}
                                onChange={(e) => setDraft({ ...draft, clientId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All clients</option>
                                {clientOptions.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Payer</span>
                            <select
                                value={draft.payerName ?? ''}
                                onChange={(e) => setDraft({ ...draft, payerName: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All payers</option>
                                {payerOptions.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">Status</span>
                            <select
                                value={draft.status ?? ''}
                                onChange={(e) => setDraft({ ...draft, status: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All statuses</option>
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">DOS from</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">DOS to</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
                        <Button variant="ghost" size="sm" onClick={clear} disabled={!hasActiveFilter && !draft.clientId && !draft.payerName && !draft.status && !draft.dateFrom && !draft.dateTo}>
                            Clear
                        </Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn't load report</p>
                        <p className="text-xs text-text-subtle mt-1">
                            {extractErrorMessage(error, 'Please try again.')}
                        </p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card>
                    <CardBody>
                        <div className="flex items-center gap-2 text-text-subtle text-sm">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading report…
                        </div>
                    </CardBody>
                </Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <Kpi label="Claims" value={data.kpis.totalClaims.toLocaleString()} />
                        <Kpi label="Billed" value={fmtMoney(data.kpis.totalBilled)} />
                        <Kpi label="Paid" value={fmtMoney(data.kpis.totalPaid)} />
                        <Kpi label="Outstanding" value={fmtMoney(data.kpis.totalOutstanding)} emphasis={data.kpis.totalOutstanding > 0} />
                        <Kpi label="Collection rate" value={fmtPct(data.kpis.collectionRate)} />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Card>
                            <CardHeader>
                                <div>
                                    <CardTitle>AR aging</CardTitle>
                                    <p className="text-2xs text-text-subtle mt-0.5">
                                        Claims by submit date, invoices by issue date.
                                    </p>
                                </div>
                            </CardHeader>
                            <CardBody className="pt-0">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Bucket</th>
                                            <th className="px-3 py-2 font-bold text-right">Open items</th>
                                            <th className="px-3 py-2 font-bold text-right">Outstanding</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.aging.map((r) => (
                                            <tr key={r.bucket} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{r.bucket} days</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {r.amount > 0 ? fmtMoneyCents(r.amount) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {data.typeBreakdown.length > 0 && (
                                    <div className="mt-3 pt-3 border-t border-border">
                                        <p className="text-2xs uppercase tracking-wider text-text-subtle px-3 mb-1">
                                            By source
                                        </p>
                                        <table className="w-full text-sm">
                                            <thead>
                                                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                    <th className="px-3 py-2 font-bold">Type</th>
                                                    <th className="px-3 py-2 font-bold">Method</th>
                                                    <th className="px-3 py-2 font-bold text-right">Count</th>
                                                    <th className="px-3 py-2 font-bold text-right">Outstanding</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.typeBreakdown.map((r) => (
                                                    <tr
                                                        key={`${r.type}-${r.method}`}
                                                        className="border-b border-border last:border-b-0"
                                                    >
                                                        <td className="px-3 py-2">{r.type}</td>
                                                        <td className="px-3 py-2 text-text-muted">{r.method}</td>
                                                        <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                        <td className="px-3 py-2 text-right tabular-nums">
                                                            {r.outstanding > 0 ? fmtMoneyCents(r.outstanding) : '—'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>

                        <Card>
                            <CardHeader><CardTitle>Status breakdown</CardTitle></CardHeader>
                            <CardBody className="pt-0">
                                {data.statusBreakdown.length === 0 ? (
                                    <p className="text-xs text-text-subtle px-3 py-4">No claims match the current filter.</p>
                                ) : (
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                                <th className="px-3 py-2 font-bold">Status</th>
                                                <th className="px-3 py-2 font-bold text-right">Claims</th>
                                                <th className="px-3 py-2 font-bold text-right">Billed</th>
                                                <th className="px-3 py-2 font-bold text-right">Paid</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.statusBreakdown.map((r) => (
                                                <tr key={r.status} className="border-b border-border last:border-b-0">
                                                    <td className="px-3 py-2"><StatusBadge status={r.status} /></td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoneyCents(r.billed)}</td>
                                                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoneyCents(r.paid)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </CardBody>
                        </Card>
                    </div>

                    <Card>
                        <CardHeader><CardTitle>By payer</CardTitle></CardHeader>
                        <CardBody className="pt-0">
                            {data.payerBreakdown.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">No claims match the current filter.</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Payer</th>
                                            <th className="px-3 py-2 font-bold text-right">Claims</th>
                                            <th className="px-3 py-2 font-bold text-right">Billed</th>
                                            <th className="px-3 py-2 font-bold text-right">Paid</th>
                                            <th className="px-3 py-2 font-bold text-right">Outstanding</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.payerBreakdown.map((r) => (
                                            <tr key={r.payer} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">{r.payer}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoneyCents(r.billed)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoneyCents(r.paid)}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {r.outstanding > 0 ? (
                                                        <span className="text-warning-text font-medium">{fmtMoneyCents(r.outstanding)}</span>
                                                    ) : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>

                    <Card>
                        <CardHeader>
                            <CardTitle>Patient-responsibility outstanding</CardTitle>
                        </CardHeader>
                        <CardBody className="pt-0">
                            {data.prOutstanding.length === 0 ? (
                                <p className="text-xs text-text-subtle px-3 py-4">
                                    No outstanding patient-responsibility balances.
                                </p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">Client</th>
                                            <th className="px-3 py-2 font-bold text-right">Open claims</th>
                                            <th className="px-3 py-2 font-bold text-right">PR balance</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.prOutstanding.map((r) => (
                                            <tr key={r.clientId ?? r.clientName} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2">
                                                    {r.clientId ? (
                                                        <Link to={`/clients/${r.clientId}`} className="hover:underline">
                                                            {r.clientName}
                                                        </Link>
                                                    ) : r.clientName}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">{r.openClaims}</td>
                                                <td className="px-3 py-2 text-right tabular-nums">{fmtMoneyCents(r.prBalance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className={`font-display text-xl font-bold mt-1 tabular-nums ${emphasis ? 'text-warning-text' : 'text-text'}`}>
                    {value}
                </p>
            </CardBody>
        </Card>
    );
}
